import { useState } from 'react';
import { useUserContext } from '../context/use-user-context';
import { useChatContext } from '../context/use-chat-context';
import { useStoreInfoStore } from '../../stores/useStoreInfoStore';
import { Featureflag } from '../constants/featureFlags';
import { useStoreHostnameStore } from '../../stores/useStoreHostnameStore';
import { getEnv } from '../../utils/env';
import { useHotkeys } from '@mantine/hooks';
import { cn } from '../utils/styles';
import { Modal } from './ModalComponent';
import { FlagsTab } from './FlagsTab';
import { DebugTab } from './DebugTab';
import type { Tab, DevToolFlag, DebugEntry } from './types';

function useDevToolFlags(): DevToolFlag[] {
  const {
    isAvailableToCurrentUser,
    originalIsAvailableToCurrentUser,
    setIsAvailableToCurrentUserOverride,
    isFeatureEnabled,
    aiFiltersOverride,
    setAiFiltersEnabledOverride,
    voiceResponsesOverride,
    setVoiceResponsesEnabledOverride,
    voiceInputEnabled,
    originalVoiceInputEnabled,
    setVoiceInputEnabledOverride,
  } = useUserContext();

  const store = useStoreInfoStore((s) => s.store);

  const originalAiFiltersEnabled = isFeatureEnabled(Featureflag.AI_FILTERS);
  const aiFiltersEnabled = aiFiltersOverride ?? originalAiFiltersEnabled;

  const originalVoiceResponsesEnabled =
    store?.config?.config?.moduleEnabled?.includes('voice-responses') ?? false;
  const voiceResponsesEnabled =
    voiceResponsesOverride ?? originalVoiceResponsesEnabled;

  return [
    {
      key: 'isAvailableToCurrentUser',
      label: 'Display Superbar',
      defaultValue: originalIsAvailableToCurrentUser,
      currentValue: isAvailableToCurrentUser,
      isOverridden:
        isAvailableToCurrentUser !== originalIsAvailableToCurrentUser,
      toggle: () =>
        setIsAvailableToCurrentUserOverride(!isAvailableToCurrentUser),
    },
    {
      key: 'aiFiltersEnabled',
      label: 'AI Filters',
      defaultValue: originalAiFiltersEnabled,
      currentValue: aiFiltersEnabled,
      isOverridden: aiFiltersEnabled !== originalAiFiltersEnabled,
      toggle: () => setAiFiltersEnabledOverride(!aiFiltersEnabled),
    },
    {
      key: 'voiceResponsesEnabled',
      label: 'Voice Responses (TTS)',
      defaultValue: originalVoiceResponsesEnabled,
      currentValue: voiceResponsesEnabled,
      isOverridden: voiceResponsesEnabled !== originalVoiceResponsesEnabled,
      toggle: () => setVoiceResponsesEnabledOverride(!voiceResponsesEnabled),
    },
    {
      key: 'voiceInputEnabled',
      label: 'Voice Input',
      defaultValue: originalVoiceInputEnabled,
      currentValue: voiceInputEnabled,
      isOverridden: voiceInputEnabled !== originalVoiceInputEnabled,
      toggle: () => setVoiceInputEnabledOverride(!voiceInputEnabled),
    },
  ];
}

function useDebugEntries(): DebugEntry[] {
  const store = useStoreInfoStore((s) => s.store);
  const session = useStoreInfoStore((s) => s.currentSession);
  const sessionSource = useStoreInfoStore((s) => s.sessionSource);
  const hostname = useStoreHostnameStore((s) => s.getHostname());
  const {
    displayMode,
    superBarViewState,
    systemMessageState,
    searchId,
    isPlayground,
    mode,
  } = useChatContext();

  return [
    { label: 'Store ID', value: store?.id ?? '-' },
    { label: 'Store Name', value: store?.name ?? '-' },
    { label: 'Hostname', value: hostname },
    { label: 'Session ID', value: session?.id ?? '-' },
    { label: 'Session Source', value: sessionSource },
    { label: 'Search ID', value: searchId ?? '-' },
    { label: 'Display Mode', value: displayMode },
    { label: 'View State', value: superBarViewState },
    { label: 'System Message', value: systemMessageState },
    { label: 'Theme', value: mode },
    { label: 'Playground', value: String(isPlayground) },
    { label: 'API Base URL', value: getEnv('BASE_API_URL') ?? '-' },
  ];
}

const TABS: { key: Tab; label: string }[] = [
  { key: 'flags', label: 'Flags' },
  { key: 'debug', label: 'Debug' },
];

export function DevTools() {
  const [opened, setOpened] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('flags');
  const { mode, setChatState } = useChatContext();
  const { isLoadingFlags } = useUserContext();
  const flags = useDevToolFlags();
  const debugEntries = useDebugEntries();

  useHotkeys([['mod+shift+X', () => setOpened(true)]], []);

  const overriddenCount = flags.filter((f) => f.isOverridden).length;

  const resetAll = () => {
    for (const flag of flags) {
      if (flag.isOverridden) flag.toggle();
    }
    setChatState({ mode: 'light' });
  };

  return (
    <Modal
      opened={opened}
      onClose={() => setOpened(false)}
      title={
        <h3 className="caddietw-text-lg caddietw-font-semibold caddietw-m-0">
          Dev Tools
        </h3>
      }
      size="sm"
      centered
    >
      <div className="caddietw-flex caddietw-flex-col caddietw-gap-4">
        {/* Tabs */}
        <div className="caddietw-flex caddietw-gap-0 caddietw-border-b caddietw-border-gray-200">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              className={cn(
                'caddietw-px-4 caddietw-py-2 caddietw-text-sm caddietw-font-medium caddietw-border-0 caddietw-border-b-2 caddietw-cursor-pointer caddietw-transition-colors caddietw-bg-transparent',
                activeTab === key
                  ? 'caddietw-border-blue-600 caddietw-text-blue-600'
                  : 'caddietw-border-transparent caddietw-text-gray-500 hover:caddietw-text-gray-700'
              )}
            >
              {label}
              {key === 'flags' && overriddenCount > 0 && (
                <span className="caddietw-ml-1.5 caddietw-text-[10px] caddietw-px-1.5 caddietw-py-0.5 caddietw-rounded-full caddietw-bg-amber-100 caddietw-text-amber-700">
                  {overriddenCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'flags' && (
          <FlagsTab
            flags={flags}
            isLoadingFlags={isLoadingFlags}
            overriddenCount={overriddenCount}
            resetAll={resetAll}
            mode={mode}
            onThemeChange={(value) => setChatState({ mode: value })}
          />
        )}
        {activeTab === 'debug' && <DebugTab entries={debugEntries} />}

        {/* Footer */}
        <div className="caddietw-text-[10px] caddietw-text-gray-400 caddietw-border-t caddietw-border-gray-100 caddietw-pt-3 caddietw-flex caddietw-justify-between">
          <span>Session only - resets on tab close</span>
          <span>
            <span style={{ fontFamily: 'system-ui' }}>
              {navigator.platform?.includes('Mac') ? '\u2318' : 'Ctrl'}
            </span>
            +Shift+X
          </span>
        </div>
      </div>
    </Modal>
  );
}

export default DevTools;
